import { BookkeepingQaApiClient } from "@/clients/bookkeepingQaApiClient";
import { BookkeepingQuestion, SubmitQuestionResponse } from "@/types/bookkeepingQa";

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'out_of_scope']);

export class BookkeepingQaService {
  private client: BookkeepingQaApiClient;

  constructor() {
    this.client = new BookkeepingQaApiClient();
  }

  /**
   * Submit a question and poll for updates until a terminal status is reached.
   * Calls onUpdate with each polled state so the UI stays live.
   */
  async submitAndPoll(
    questionText: string,
    onUpdate: (q: BookkeepingQuestion) => void
  ): Promise<BookkeepingQuestion> {
    const { questionId }: SubmitQuestionResponse = await this.client.submitQuestion(questionText);

    const placeholder: BookkeepingQuestion = {
      id: questionId,
      questionText,
      answerText: null,
      status: 'queued',
      errorMessage: null,
      createdAt: new Date().toISOString(),
      answeredAt: null,
    };
    onUpdate(placeholder);

    return new Promise<BookkeepingQuestion>((resolve, reject) => {
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const cleanup = () => {
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      intervalId = setInterval(async () => {
        try {
          const question = await this.client.getQuestion(questionId);
          onUpdate(question);

          if (TERMINAL_STATUSES.has(question.status)) {
            cleanup();
            resolve(question);
          }
        } catch (error) {
          cleanup();
          reject(error);
        }
      }, POLL_INTERVAL_MS);
    });
  }

  /**
   * Load question history with optional pagination.
   */
  async loadHistory(
    pagination?: { limit: number; offset: number }
  ): Promise<{ questions: BookkeepingQuestion[]; total: number }> {
    return this.client.listQuestions(pagination);
  }
}
